/**
 * Typed bindings + React Query hooks for /api/admin/* (superadmin endpoints).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "./api";
import { ME_QUERY_KEY, type CurrentUser } from "./auth";

export type CompanyStatus = "active" | "pending_suspension" | "suspended";

export interface CompanySummary {
  id: string;
  slug: string;
  name: string;
  status: CompanyStatus;
  suspension_scheduled_at: string | null;
  notes: string | null;
  created_at: string;
  user_count: number;
}

export interface CompanyDetail extends CompanySummary {
  suspended_reason: string | null;
  owner_user_id: string | null;
}

export interface CompanyCreatePayload {
  name: string;
  slug: string;
  admin_username: string;
  notes?: string | null;
}

export interface CompanyCreateResponse {
  company: CompanyDetail;
  invite_url: string;
  invite_expires_at: string;
}

export const COMPANIES_QUERY_KEY = ["admin", "companies"] as const;

export function useCompanies() {
  return useQuery<CompanySummary[]>({
    queryKey: COMPANIES_QUERY_KEY,
    queryFn: () => api.get<CompanySummary[]>("/api/admin/companies"),
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CompanyCreatePayload) =>
      api.post<CompanyCreateResponse>("/api/admin/companies", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY }),
  });
}

export function useSuspendCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      reason?: string;
      immediate?: boolean;
      /** Absolute ISO instant when the company should freeze. */
      freeze_at?: string;
      /** Or an offset from now, in hours. */
      in_hours?: number;
    }) =>
      api.post<CompanyDetail>(`/api/admin/companies/${vars.id}/suspend`, {
        reason: vars.reason,
        immediate: vars.immediate ?? false,
        freeze_at: vars.freeze_at,
        in_hours: vars.in_hours,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY }),
  });
}

export function useUnsuspendCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<CompanyDetail>(`/api/admin/companies/${id}/unsuspend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY }),
  });
}

export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; confirmation_slug: string }) =>
      api.delete<void>(`/api/admin/companies/${vars.id}`, {
        confirmation_slug: vars.confirmation_slug,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY }),
  });
}

export interface ImpersonateResponse {
  company_id: string;
  company_slug: string;
  company_name: string;
}

/**
 * Fetch /me and shove it into the React Query cache *synchronously* via
 * setQueryData. Using refetchQueries here means the cache update arrives
 * via the store's notify callback and the next React render happens a tick
 * later — long enough that subsequent `pushState + popstate` reaches the
 * router with the *old* /me value. Manually setting the data side-steps
 * the race so the very next render after the mutation already sees the
 * impersonation context.
 */
async function refreshMeIntoCache(qc: ReturnType<typeof useQueryClient>): Promise<void> {
  let me: CurrentUser | null = null;
  try {
    me = await api.get<CurrentUser>("/api/auth/me");
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      me = null;
    } else {
      throw err;
    }
  }
  qc.setQueryData(ME_QUERY_KEY, me);
}

export function useEnterCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<ImpersonateResponse>(
        `/api/admin/companies/${id}/impersonate`,
      );
      // Drop every cached company-scoped query so the company shell
      // doesn't render with stale data from the previous tenant context.
      qc.removeQueries({
        predicate: (q) => q.queryKey[0] !== ME_QUERY_KEY[0],
      });
      // Refresh /me synchronously — see comment on refreshMeIntoCache.
      await refreshMeIntoCache(qc);
      return res;
    },
  });
}

export function useExitImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post<void>(`/api/admin/companies/exit-impersonation`);
      qc.removeQueries({
        predicate: (q) => q.queryKey[0] !== ME_QUERY_KEY[0],
      });
      await refreshMeIntoCache(qc);
    },
  });
}
